import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Users,
  Scale,
  Pencil,
  PlusCircle,
  List,
  MapPin,
  Building2,
  Archive,
  Trash2,
  CircleHelp,
  ChevronLeft,
  ChevronRight,
  Settings2,
  GripVertical,
  RotateCcw,
} from "lucide-react";
import { api, money, dateFmt } from "../lib/api.js";
import { Card, Pill, Empty, Modal } from "../components/UI.jsx";
import CurrencySelect from "../components/CurrencySelect.jsx";
import MovementModal from "../components/MovementModal.jsx";

const DETAIL_COLUMNS=[{key:"date",label:"Date",width:120,visible:true},{key:"description",label:"Description",width:330,visible:true},{key:"category",label:"Category",width:175,visible:true},{key:"type",label:"Type",width:140,visible:true},{key:"bucket",label:"50/30/20",width:120,visible:true},{key:"status",label:"Status",width:135,visible:true},{key:"amount",label:"Amount",width:145,visible:true},{key:"actions",label:"Actions",width:92,visible:true}];
const movementPrefs=value=>{try{const p=typeof value==="string"?JSON.parse(value):value;if(p?.columns?.length)return{columns:p.columns.filter(c=>c.key!=="account"),density:p.density||"normal"}}catch{}return{columns:DETAIL_COLUMNS,density:"normal"}};

export default function AccountDetail({ user, onAddMovement }) {
  const { id } = useParams(),
    nav = useNavigate();
  const [a, setA] = useState(null),
    [txs, setTxs] = useState([]),
    [rec, setRec] = useState([]),
    [cfg, setCfg] = useState(null),
    [modal, setModal] = useState(null),
    [editingMovement, setEditingMovement] = useState(null),
    [movementColumns,setMovementColumns]=useState(DETAIL_COLUMNS),
    [movementDensity,setMovementDensity]=useState("normal"),
    [columnModal,setColumnModal]=useState(false),
    [dragColumn,setDragColumn]=useState(null),
    [movementFilter,setMovementFilter]=useState(null),
    [msg, setMsg] = useState(""),
    [error, setError] = useState("");
  const [f, setF] = useState({
    statementDate: new Date().toISOString().slice(0, 10),
    statementBalance: "",
    email: "",
    permission: "view",
  });
  const [edit, setEdit] = useState({});
  const [legacyHolding,setLegacyHolding]=useState({name:"",assetType:"stock",symbol:"",quantity:1});
  const load = () =>
    Promise.all([
      api("/accounts/" + id),
      api("/transactions?accountId=" + id + "&limit=50"),
      api("/reconciliations"),
      api("/config"),
    ])
      .then(([x, t, r, c]) => {
        setA(x);
        setTxs(t);
        setRec(r.filter((z) => String(z.account_id) === String(id)));
        setCfg(c);
        const prefs=movementPrefs(c?.preferences?.movement_columns_json);setMovementColumns(prefs.columns);setMovementDensity(prefs.density);
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, [id]);
  useEffect(() => { const refresh=()=>load(); window.addEventListener("pisto:data-changed",refresh); return()=>window.removeEventListener("pisto:data-changed",refresh); }, [id]);
  const deleteMovement = async (x) => {
    const linked = Boolean(x.transfer_group_id);
    if (!confirm(linked ? "Delete both sides of this linked transfer?" : "Delete this movement?")) return;
    try {
      await api(linked ? `/transfers/${x.transfer_group_id}?accountId=${x.account_id}` : `/transactions/${x.id}?accountId=${x.account_id}`, { method:"DELETE" });
      load(); window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) { setError(e.message); }
  };
  const visibleMovements=txs.filter(x=>!movementFilter||(movementFilter.type==="category"?(String(x.category_id)===String(movementFilter.value)||(x.additional_categories||[]).some(c=>String(c.id)===String(movementFilter.value))):movementFilter.type==="status"?(x.clearance_status||"not_cleared")===movementFilter.value:movementFilter.type==="financialType"?x.financial_type===movementFilter.value:movementFilter.type==="tag"?(x.tags||[]).includes(movementFilter.value):true));
  const displayedColumns=movementColumns.filter(c=>c.visible&&c.key!=="account");
  const dropColumn=target=>{if(!dragColumn||dragColumn===target)return;setMovementColumns(cols=>{const next=[...cols],from=next.findIndex(c=>c.key===dragColumn),to=next.findIndex(c=>c.key===target),[item]=next.splice(from,1);next.splice(to,0,item);return next});setDragColumn(null)};
  const resizeColumn=(e,key)=>{e.preventDefault();e.stopPropagation();const start=e.clientX,initial=movementColumns.find(c=>c.key===key)?.width||120;const move=ev=>setMovementColumns(cols=>cols.map(c=>c.key===key?{...c,width:Math.max(70,Math.min(700,initial+ev.clientX-start))}:c));const up=()=>{window.removeEventListener("mousemove",move);window.removeEventListener("mouseup",up)};window.addEventListener("mousemove",move);window.addEventListener("mouseup",up)};
  const saveMovementColumns=async()=>{const actions=movementColumns.find(c=>c.key==="actions")||DETAIL_COLUMNS.at(-1);const detail=[...movementColumns.filter(c=>c.key!=="actions"),{...actions,visible:true}],columns=[{key:"account",label:"Account",width:150,visible:true},...detail];await api("/preferences/movement-columns",{method:"PUT",body:{columns,density:movementDensity}});setMovementColumns(detail);setColumnModal(false)};
  const movementCell=(x,key)=>key==="date"?dateFmt(x.operation_date):key==="description"?<div className="movement-description"><b>{x.description||"—"}</b><div className="table-tags">{(x.tags||[]).map(tag=><Pill key={tag} onClick={e=>{e.stopPropagation();setMovementFilter({type:"tag",value:tag,label:tag})}}>{tag}</Pill>)}</div></div>:key==="category"?<div className="table-tags">{x.category_name?<Pill onClick={e=>{e.stopPropagation();setMovementFilter({type:"category",value:x.category_id,label:x.category_name})}}>{x.category_name}</Pill>:"—"}{(x.additional_categories||[]).map(c=><Pill key={c.id} onClick={e=>{e.stopPropagation();setMovementFilter({type:"category",value:c.id,label:c.name})}}>{c.name}</Pill>)}</div>:key==="status"?<Pill onClick={e=>{e.stopPropagation();setMovementFilter({type:"status",value:x.clearance_status||"not_cleared",label:String(x.clearance_status||"not_cleared").replaceAll("_"," ")})}}>{String(x.clearance_status||"not_cleared").replaceAll("_"," ")}</Pill>:key==="type"?<Pill tone={x.financial_type==="passive_income"?"green":x.financial_type==="expense"?"red":""} onClick={e=>{e.stopPropagation();setMovementFilter({type:"financialType",value:x.financial_type,label:x.asset_role==="purchase"?"Investment purchase":x.asset_role==="sale"?"Investment sale":String(x.financial_type||"").replaceAll("_"," ")})}}>{x.asset_role==="purchase"?"Investment purchase":x.asset_role==="sale"?"Investment sale":String(x.financial_type||"").replaceAll("_"," ")}</Pill>:key==="bucket"?<Pill>{String(x.budget_bucket||"unclassified").replaceAll("_"," ")}</Pill>:key==="amount"?money(x.amount,x.currency):<div className="row-actions"><button className="icon" title="Edit movement" onClick={e=>{e.stopPropagation();setEditingMovement(x)}}><Pencil size={15}/></button><button className="icon" title="Delete movement" onClick={e=>{e.stopPropagation();deleteMovement(x)}}><Trash2 size={15}/></button></div>;
  const reconcile = async (e) => {
    e.preventDefault();
    try {
      const r = await api("/reconciliations", {
        method: "POST",
        body: {
          accountId: Number(id),
          statementDate: f.statementDate,
          statementBalance: Number(f.statementBalance),
        },
      });
      setMsg(
        r.status === "reconciled"
          ? "Balance checkpoint matched. This account is reconciled to that date."
          : `${r.difference > 0 ? "History still missing" : "Recorded history exceeds the checkpoint"}: ${money(Math.abs(r.difference), a.currency)}`,
      );
      setModal(null);
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const deleteCheckpoint = async (checkpoint) => {
    if (!confirm(`Delete the balance checkpoint for ${dateFmt(checkpoint.statement_date)}? Account totals and unexplained differences will be recalculated.`)) return;
    try {
      await api(`/reconciliations/${checkpoint.id}`, { method: "DELETE" });
      setMsg("Balance checkpoint deleted.");
      await load();
      window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) { setError(e.message); }
  };
  const share = async (e) => {
    e.preventDefault();
    try {
      await api(`/accounts/${id}/share`, {
        method: "POST",
        body: { email: f.email, permission: f.permission },
      });
      setMsg(`Access granted to ${f.email}.`);
      setModal(null);
    } catch (e) {
      setError(e.message);
    }
  };
  const openEdit = () => {
    setEdit({
      name: a.name,
      accountType: a.account_type,
      currency: a.currency,
      identifier: a.identifier || "",
      bankName: a.bank_name || "",
      costCenterId: a.cost_center_id ? String(a.cost_center_id) : "",
      locationId: a.location_id ? String(a.location_id) : "",
      isLiability: Boolean(a.is_liability),
      isActive: a.is_active !== 0,
      includeInTotals: a.include_in_totals !== 0,
    });
    setModal("edit");
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api(`/accounts/${id}`, {
        method: "PUT",
        body: {
          ...edit,
          costCenterId: edit.costCenterId || null,
          locationId: edit.locationId || null,
        },
      });
      setModal(null);
      setMsg("Account settings updated.");
      load();
      window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) {
      setError(e.message);
    }
  };
  const toggleActive = async () => {
    const next = !(a.is_active !== 0);
    if (!window.confirm(`${next ? "Activate" : "Deactivate"} ${a.name}?`))
      return;
    try {
      await api(`/accounts/${id}`, {
        method: "PUT",
        body: {
          name: a.name,
          accountType: a.account_type,
          currency: a.currency,
          identifier: a.identifier || "",
          costCenterId: a.cost_center_id || null,
          locationId: a.location_id || null,
          isLiability: Boolean(a.is_liability),
          isActive: next,
          includeInTotals: a.include_in_totals !== 0,
        },
      });
      load();
      window.dispatchEvent(new Event("pisto:data-changed"));
    } catch (e) {
      setError(e.message);
    }
  };
  const closeAccount = async () => {
    if (
      !confirm(`Close ${a.name}? Its movements will remain visible but frozen.`)
    )
      return;
    try {
      await api(`/accounts/${id}/close`, { method: "POST", body: {} });
      setMsg("Account closed. Its history is retained and frozen.");
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const deleteAccount = async () => {
    if (
      !confirm(
        `Delete ${a.name} and all its movements? An encrypted recovery copy will be available for 30 days.`,
      )
    )
      return;
    try {
      await api(`/accounts/${id}`, { method: "DELETE" });
      nav("/accounts");
    } catch (e) {
      setError(e.message);
    }
  };
  const openLegacyConversion=()=>{setLegacyHolding({name:a.name,assetType:"stock",symbol:"",quantity:1});setModal("legacy-holding")};
  const convertLegacyHolding=async e=>{e.preventDefault();try{const result=await api(`/accounts/${id}/convert-legacy-holding`,{method:"POST",body:legacyHolding});nav(`/investments?assetId=${result.assetId}`);window.dispatchEvent(new Event("pisto:data-changed"))}catch(err){setError(err.message)}};
  if (error && !a) return <div className="error">{error}</div>;
  if (!a) return <div className="loading">Loading account…</div>;
  const latest = rec[0],
    canAdmin =
      Number(a.owner_user_id) === Number(user.id) ||
      a.access_permission === "admin";
  const siblings = (cfg?.accounts || []).filter((x) => String(x.location_id || x.bank_id || "") === String(a.location_id || a.bank_id || ""));
  const siblingIndex = siblings.findIndex((x) => String(x.id) === String(id));
  return (
    <>
      <button className="text-button" onClick={() => nav("/accounts")}>
        <ArrowLeft size={15} /> Accounts
      </button>
      {siblings.length > 1 && <div className="account-sibling-nav"><button className="secondary compact" disabled={siblingIndex <= 0} onClick={()=>nav(`/accounts/${siblings[siblingIndex-1].id}`)}><ChevronLeft size={15}/> Previous account</button><span>{siblingIndex+1} of {siblings.length} in {a.location?.name || a.bank_name}</span><button className="secondary compact" disabled={siblingIndex < 0 || siblingIndex >= siblings.length-1} onClick={()=>nav(`/accounts/${siblings[siblingIndex+1].id}`)}>Next account <ChevronRight size={15}/></button></div>}
      <div className="page-head">
        <div>
          <p className="eyebrow">
            {a.cost_center_name ? `${a.cost_center_name} › ` : ""}
            {a.location?.name || a.bank_name || a.account_type}
          </p>
          <h1>{a.name}</h1>
          <p>
            {a.identifier || "No account identifier stored"} · {a.currency}
          </p>
        </div>
        <div className="actions">
          <button
            className="primary"
            onClick={() =>
              onAddMovement?.({
                accountId: Number(id),
                source: "account-detail",
              })
            }
          >
            <PlusCircle size={16} /> Add movement
          </button>
          {canAdmin && (
            <button className="secondary" onClick={openEdit}>
              <Pencil size={16} /> Edit
            </button>
          )}
          {canAdmin && a.account_type === "investment" && !a.holdings?.length && <button className="secondary" onClick={openLegacyConversion}><Archive size={16}/> Convert old asset account</button>}
          {canAdmin && (
            <button className="secondary" onClick={() => setModal("share")}>
              <Users size={16} /> Share
            </button>
          )}
          <button className="secondary" onClick={() => setModal("reconcile")}>
            <Scale size={16} /> Add balance checkpoint
          </button>
          {canAdmin && a.is_active !== 0 && (
            <button className="secondary" onClick={closeAccount}>
              <Archive size={16} /> Close account
            </button>
          )}
          {canAdmin && (
            <button className="secondary danger" onClick={deleteAccount}>
              <Trash2 size={16} /> Delete
            </button>
          )}
        </div>
      </div>
      {msg && <div className="notice">{msg}</div>}
      {error && <div className="error">{error}</div>}
      <div className="account-detail-grid">
        <Card className="metric-card">
          <span>Current balance</span>
          <strong>{money(a.current_balance, a.currency)}</strong>
          <div className="pills">
            <Pill>{a.account_type.replace("_", " ")}</Pill>
            <Pill
              tone={a.is_active ? "green" : "red"}
              onClick={canAdmin ? toggleActive : undefined}
              title={canAdmin ? "Click to change account status" : ""}
            >
              {a.is_active ? "Active" : "Inactive"}
            </Pill>
            {a.include_in_totals ? (
              <Pill tone="green">Included in totals</Pill>
            ) : (
              <Pill>Excluded from totals</Pill>
            )}
          </div>
        </Card>
        <Card className="metric-card">
          <span>Where it belongs</span>
          <strong className="status-line">
            <Building2 /> {a.cost_center_name || "No cost center"}
          </strong>
          <small className="status-line">
            <MapPin size={15} />{" "}
            {a.location?.name || a.bank_name || "No place selected"}
          </small>
          {a.location?.default_cost_center_id &&
            String(a.location.default_cost_center_id) !==
              String(a.cost_center_id || "") && (
              <Pill
                tone="yellow"
                title="This account cost center does not match the default configured for its place."
              >
                <AlertTriangle size={12} /> Cost center mismatch
              </Pill>
            )}
        </Card>
        <Card className="metric-card">
          <span className="status-line">Balance checkpoint <CircleHelp size={15} title="The real bank balance on a chosen date. Older movements added later recalculate the unexplained difference without changing today's displayed balance." /></span>
          <strong className="status-line">
            {latest?.status === "reconciled" ? (
              <CheckCircle2 />
            ) : (
              <AlertTriangle />
            )}
            {latest?.status === "reconciled" ? "Reconciled" : latest ? "History incomplete" : "No checkpoint"}
          </strong>
          {latest ? (
            <Pill tone={latest.status === "reconciled" ? "green" : "red"}>
              {latest.status === "reconciled"
                ? "Reconciled"
                : `${latest.difference_direction === "missing" ? "Still to explain" : "Recorded too much"} ${money(Math.abs(latest.difference), a.currency)}`}
            </Pill>
          ) : (
            <Pill>Add today’s real bank balance</Pill>
          )}
          {latest && <small>Checkpoint {dateFmt(latest.statement_date)}: {money(latest.statement_balance, a.currency)} · History explains {money(latest.calculated_balance, a.currency)}</small>}
        </Card>
      </div>
      {a.account_type === "investment" && <Card className="table-card investment-account-holdings">
        <div className="inner-title"><div><h2>Holdings in this investment account</h2><p className="muted">One brokerage account can contain many stocks, funds and bonds. Each holding is tracked separately without becoming another account.</p></div><Pill>{a.holdings?.length||0} holdings</Pill></div>
        <button className="primary compact" onClick={()=>nav(`/investments?newHoldingAccountId=${a.id}`)}><PlusCircle size={15}/> Add holding / transaction</button>
        {!a.holdings?.length?<Empty title="No holdings linked yet" text="Add each stock, ETF, fund or bond as a holding inside this brokerage account."/>:<div className="table-wrap"><table><thead><tr><th>Holding</th><th>Type</th><th className="right">Quantity</th><th className="right">Average cost</th><th className="right">Market price</th><th className="right">Current value</th></tr></thead><tbody>{a.holdings.map(h=><tr key={h.id} onClick={()=>nav(`/investments?assetId=${h.id}`)} className="movement-row"><td><b>{h.name}</b><small className="cell-sub">{h.symbol||"Manual valuation"}</small></td><td>{String(h.asset_type).replaceAll("_"," ")}</td><td className="right">{Number(h.quantity||0)}</td><td className="right">{Number(h.quantity||0)?money(Number(h.cost_basis||0)/Number(h.quantity),h.currency):"—"}</td><td className="right">{h.current_price==null?"—":money(h.current_price,h.currency)}</td><td className="right amount">{money(h.current_value||0,h.currency)}</td></tr>)}</tbody></table></div>}
      </Card>}
      {rec.length > 0 && (
        <Card className="table-card">
          <div className="inner-title"><div><h2>Checkpoint history</h2><p className="muted">These differences recalculate whenever older movements are added or changed.</p></div></div>
          <div className="table-wrap"><table><thead><tr><th>Date</th><th>Real bank balance</th><th>History explains</th><th>Result</th><th>Actions</th></tr></thead><tbody>
            {rec.map((checkpoint) => <tr key={checkpoint.id}>
              <td>{dateFmt(checkpoint.statement_date)}</td>
              <td>{money(checkpoint.statement_balance, a.currency)}</td>
              <td>{money(checkpoint.calculated_balance, a.currency)}</td>
              <td><Pill tone={checkpoint.status === "reconciled" ? "green" : "red"}>{checkpoint.status === "reconciled" ? "Reconciled" : `${checkpoint.difference_direction === "missing" ? "Still to explain" : "Recorded too much"} ${money(Math.abs(checkpoint.difference), a.currency)}`}</Pill></td>
              <td><button className="icon" type="button" title="Delete checkpoint" onClick={()=>deleteCheckpoint(checkpoint)}><Trash2 size={15}/></button></td>
            </tr>)}
          </tbody></table></div>
        </Card>
      )}
      <Card className="table-card">
        <div className="inner-title">
          <h2>Recent movements</h2>
          <div className="row-actions">
            <Pill>{visibleMovements.length} loaded</Pill>
            {movementFilter&&<button className="secondary compact" onClick={()=>setMovementFilter(null)}>Clear {movementFilter.label}</button>}
            <button className="secondary compact" onClick={()=>setColumnModal(true)}><Settings2 size={15}/> Configure columns</button>
            <button
              className="secondary compact"
              onClick={() => nav(`/movements?accountId=${id}`)}
            >
              <List size={15} /> View all movements
            </button>
          </div>
        </div>
        {!visibleMovements.length ? (
          <Empty title="No movements in this account" />
        ) : (
          <div className={`table-wrap movements-table-wrap density-${movementDensity}`}>
            <table className="movements-table"><colgroup>{displayedColumns.map(c=><col key={c.key} style={{width:c.width}}/>)}</colgroup><thead><tr>{displayedColumns.map(c=><th key={c.key} className={c.key==="actions"?"sticky-actions":c.key==="amount"?"right":""} draggable={c.key!=="actions"} onDragStart={()=>setDragColumn(c.key)} onDragOver={e=>e.preventDefault()} onDrop={()=>dropColumn(c.key)}><div className="movement-column-head">{c.label}{c.key!=="actions"&&<span className="column-resizer" onMouseDown={e=>resizeColumn(e,c.key)}/>}</div></th>)}</tr></thead>
              <tbody>{visibleMovements.map(x=><tr className="movement-row" key={x.id} onClick={()=>setEditingMovement(x)}>{displayedColumns.map(c=><td key={c.key} className={c.key==="actions"?"sticky-actions":c.key==="amount"?`right amount ${Number(x.amount)>=0?"positive":"negative"}`:""}>{movementCell(x,c.key)}</td>)}</tr>)}</tbody>
            </table>
          </div>
        )}
      </Card>
      {columnModal&&<Modal title="Configure movement columns" screenId="D0114" onClose={()=>setColumnModal(false)}><div className="column-config"><p className="muted">Drag rows to change order. Resize columns from the table header. Actions always remains visible.</p><label>Row density<select value={movementDensity} onChange={e=>setMovementDensity(e.target.value)}><option value="compact">Compact</option><option value="normal">Normal</option><option value="comfortable">Comfortable</option></select></label><div className="column-config-list">{movementColumns.map(c=><div key={c.key} draggable={c.key!=="actions"} onDragStart={()=>setDragColumn(c.key)} onDragOver={e=>e.preventDefault()} onDrop={()=>dropColumn(c.key)}><GripVertical size={17}/><label className="check"><input type="checkbox" checked={c.visible} disabled={c.key==="actions"} onChange={e=>setMovementColumns(cols=>cols.map(x=>x.key===c.key?{...x,visible:e.target.checked}:x))}/>{c.label}</label><span>{Math.round(c.width)} px</span></div>)}</div><div className="review-nav"><button className="secondary" onClick={()=>{setMovementColumns(DETAIL_COLUMNS);setMovementDensity("normal")}}><RotateCcw size={15}/> Restore defaults</button><button className="primary" onClick={saveMovementColumns}>Save columns</button></div></div></Modal>}
      {editingMovement && <MovementModal open initial={editingMovement} context={{accountId:editingMovement.account_id}} onClose={()=>setEditingMovement(null)} onSaved={()=>{setEditingMovement(null);load()}}/>}
      {modal === "reconcile" && (
        <Modal
          title="Add balance checkpoint"
          screenId="D0112"
          onClose={() => setModal(null)}
        >
          <form onSubmit={reconcile} className="form-grid">
            <label>
              Checkpoint date
              <input
                type="date"
                value={f.statementDate}
                onChange={(e) => setF({ ...f, statementDate: e.target.value })}
              />
            </label>
            <label>
              Real bank balance on that date
              <input
                required
                type="number"
                step="0.01"
                value={f.statementBalance}
                onChange={(e) =>
                  setF({ ...f, statementBalance: e.target.value })
                }
              />
            </label>
            <div className="notice span2"><b>What happens?</b> This balance becomes the account’s official balance. Older movements added later improve the history and reduce the unexplained difference, but do not change today’s balance.</div>
            {rec.some(x=>String(x.statement_date).slice(0,10)===f.statementDate) && <div className="notice info span2"><b>A checkpoint already exists for this date.</b> Saving will replace it.</div>}
            <button className="primary span2">Save checkpoint</button>
          </form>
        </Modal>
      )}
      {modal === "share" && (
        <Modal
          title="Share account"
          screenId="D0113"
          onClose={() => setModal(null)}
        >
          <form onSubmit={share} className="form-grid">
            <label>
              User email
              <input
                type="email"
                required
                value={f.email}
                onChange={(e) => setF({ ...f, email: e.target.value })}
              />
            </label>
            <label>
              Permission
              <select
                value={f.permission}
                onChange={(e) => setF({ ...f, permission: e.target.value })}
              >
                <option value="view">View</option>
                <option value="add">Add transactions</option>
                <option value="edit">Edit</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button className="primary span2">Grant access</button>
          </form>
        </Modal>
      )}
      {modal === "legacy-holding" && <Modal title="Convert old asset account into a holding" screenId="D0115" onClose={()=>setModal(null)}><form className="form-grid" onSubmit={convertLegacyHolding}><div className="span2 notice"><b>This removes the old account from Accounts without deleting its history.</b><br/>Pisto creates a holding, links the old movements to it, excludes the obsolete account from totals, and preserves it internally for audit and recovery.</div><label>Holding name<input required value={legacyHolding.name} onChange={e=>setLegacyHolding({...legacyHolding,name:e.target.value})}/></label><label>Asset type<select value={legacyHolding.assetType} onChange={e=>setLegacyHolding({...legacyHolding,assetType:e.target.value})}>{[["stock","Stock"],["etf","ETF"],["fund","Fund"],["bond","Bond"],["crypto","Crypto"],["precious_metals","Gold / precious metal"],["private_company","Private company"],["other","Other asset"]].map(([v,n])=><option key={v} value={v}>{n}</option>)}</select></label><label>Ticker / code<input value={legacyHolding.symbol} onChange={e=>setLegacyHolding({...legacyHolding,symbol:e.target.value.toUpperCase()})}/></label><label>Quantity<input type="number" min="0" step="any" value={legacyHolding.quantity} onChange={e=>setLegacyHolding({...legacyHolding,quantity:e.target.value})}/><small>You can correct quantity and purchase history from the new holding page.</small></label><div className="wizard-nav span2"><button type="button" className="secondary" onClick={()=>setModal(null)}>Cancel</button><button className="primary">Convert and open holding</button></div></form></Modal>}
      {modal === "edit" && cfg && (
        <Modal
          title="Edit account"
          screenId="D0111"
          onClose={() => setModal(null)}
        >
          <form onSubmit={saveEdit} className="form-grid">
            <label>
              Account name
              <input
                required
                value={edit.name}
                onChange={(e) => setEdit({ ...edit, name: e.target.value })}
              />
            </label>
            <label>
              Type
              <select
                value={edit.accountType}
                onChange={(e) =>
                  setEdit({ ...edit, accountType: e.target.value })
                }
              >
                {[
                  "checking",
                  "savings",
                  "credit_card",
                  "cash",
                  "investment",
                  "loan",
                  "mortgage",
                  "other",
                ].map((x) => (
                  <option value={x} key={x}>
                    {x.replaceAll("_", " ")}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Currency
              <CurrencySelect
                disabled={Math.abs(Number(a.current_balance || 0)) > 0.0001}
                value={edit.currency}
                onChange={(currency) => setEdit({ ...edit, currency })}
              />
              {Math.abs(Number(a.current_balance || 0)) > 0.0001 && (
                <small>Currency is locked while this account has funds.</small>
              )}
            </label>
            <label>
              Identifier
              <input
                value={edit.identifier}
                onChange={(e) =>
                  setEdit({ ...edit, identifier: e.target.value })
                }
              />
            </label>
            <label>
              Cost center
              <select
                value={edit.costCenterId}
                onChange={(e) =>
                  setEdit({ ...edit, costCenterId: e.target.value })
                }
              >
                <option value="">None</option>
                {cfg.costCenters
                  .filter((x) => x.is_active !== 0)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Place / location
              <select
                value={edit.locationId}
                onChange={(e) => {
                  const loc = cfg.locations.find(
                    (x) => String(x.id) === e.target.value,
                  );
                  setEdit({
                    ...edit,
                    locationId: e.target.value,
                    costCenterId: loc?.default_cost_center_id
                      ? String(loc.default_cost_center_id)
                      : edit.costCenterId,
                  });
                }}
              >
                <option value="">None</option>
                {(cfg.locations || [])
                  .filter((x) => x.is_active !== 0)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name} · {x.location_type}
                    </option>
                  ))}
              </select>
            </label>
            <label
              className="check"
              title="Inactive accounts stay in history but are hidden from normal account selection."
            >
              <input
                type="checkbox"
                checked={edit.isActive}
                onChange={(e) =>
                  setEdit({ ...edit, isActive: e.target.checked })
                }
              />{" "}
              Active account
            </label>
            <label
              className="check"
              title="Include this account when Pisto calculates your total cash balance and net worth."
            >
              <input
                type="checkbox"
                checked={edit.includeInTotals}
                onChange={(e) =>
                  setEdit({ ...edit, includeInTotals: e.target.checked })
                }
              />{" "}
              Include in my totals
            </label>
            <label
              className="check span2"
              title="A liability is money you owe. Pisto subtracts liabilities from net worth."
            >
              <input
                type="checkbox"
                checked={edit.isLiability}
                onChange={(e) =>
                  setEdit({ ...edit, isLiability: e.target.checked })
                }
              />{" "}
              Treat as liability
            </label>
            <button className="primary span2">Save account</button>
          </form>
        </Modal>
      )}
    </>
  );
}
